import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Alert,
  Link,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { monitoringAPI } from '../services/api';
import { getMOSChipColor, getMOSQualityText, getMOSColorHex } from '../utils/mosUtils';

const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to = ((endEpoch || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

export default function CallDetailPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [ callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCallData();
  }, [callId]);

  const fetchCallData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await monitoringAPI.get(`/dashboard/audio-quality/call/${callId}`);
      setCallData(response.data);
    } catch (err) {
      console.error('Error fetching call detail:', err);
      setError(err.response?.data?.detail || 'Failed to load call details');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (epoch) => {
    if (!epoch) return 'N/A';
    return new Date(epoch * 1000).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Prepare RTCP data for graphing
  const rtcpOutbound = callData?.rtcp_data?.outbound || [];
  const rtcpInbound = callData?.rtcp_data?.inbound || [];
  
  // Format data for charts
  const chartData = rtcpOutbound.map((point, idx) => ({
    time: new Date(point.timestamp * 1000).toLocaleTimeString(),
    jitter: point.jitter_ms,
    packetLoss: point.packet_loss_pct,
    mos: point.mos,
    bytesSent: point.bytes_sent,
    packetsSent: point.packets_sent
  }));

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" gutterBottom>
            Call Detail Analysis
          </Typography>
          <Tooltip title="SIP / CDR Trace">
            <IconButton onClick={() => navigate(`/cdr/${callData?.call_id}`)} sx={{ mb: 1 }}>
              <ManageSearchIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Call ID: {callId}
        </Typography>
      </Box>

      {/* Call Metadata Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Call Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">From</Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {callData?.from_user || 'N/A'}
            </Typography>

            <Typography variant="body2" color="text.secondary">To</Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {buildHomerUrl(callData?.call_id, callData?.start_time, callData?.end_time) ? (
                <Link
                  href={buildHomerUrl(callData?.call_id, callData?.start_time, callData?.end_time)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {callData?.to_user || 'N/A'}
                </Link>
              ) : (callData?.to_user || 'N/A')}
            </Typography>

            <Typography variant="body2" color="text.secondary">Switch/Cluster</Typography>
            <Chip label={callData?.switchname || 'N/A'} size="small" sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Start Time</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatTimestamp(callData?.start_time)}
            </Typography>

            <Typography variant="body2" color="text.secondary">End Time</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatTimestamp(callData?.end_time)}
            </Typography>

            <Typography variant="body2" color="text.secondary">Duration / Billable</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {callData?.duration || 0}s / {callData?.billsec || 0}s
            </Typography>

            <Typography variant="body2" color="text.secondary">Hangup Cause</Typography>
            <Chip label={callData?.hangup_cause || 'UNKNOWN'} size="small" sx={{ mb: 2 }} />
          </Grid>
        </Grid>

        {/* QoS Summary */}
        <Box sx={{ mt: 3, borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Quality of Service Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">CDR MOS</Typography>
                  <Tooltip title={getMOSQualityText(callData?.cdr_mos)} arrow>
                    <Typography variant="h5">
                      <Chip 
                        label={callData?.cdr_mos?.toFixed(2) || 'N/A'} 
                        color={getMOSChipColor(callData?.cdr_mos)}
                      />
                    </Typography>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Jitter</Typography>
                  <Typography variant="h6">
                    {callData?.cdr_jitter?.toFixed(2) || 'N/A'} ms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Packet Loss</Typography>
                  <Typography variant="h6">
                    {callData?.cdr_packet_loss?.toFixed(2) || 'N/A'} %
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Quality %</Typography>
                  <Typography variant="h6">
                    {callData?.cdr_quality_pct?.toFixed(1) || 'N/A'} %
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* RTCP Metrics Graphs */}
      {callData?.has_homer_data && chartData.length > 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            RTCP Metrics Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Real-time quality metrics from Homer RTCP reports ({chartData.length} data points)
          </Typography>

          {/* Jitter Graph */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Jitter
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis label={{ value: 'ms', angle: -90, position: 'insideLeft' }} />
                <RechartsTooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="jitter" 
                  stroke="#ff9800" 
                  name="Jitter (ms)" 
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* MOS Graph */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              MOS Score
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis domain={[0, 5]} />
                <RechartsTooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="mos" 
                  stroke="#82ca9d" 
                  name="MOS" 
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Packet Loss Graph */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Packet Loss
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis label={{ value: '%', angle: -90, position: 'insideLeft' }} />
                <RechartsTooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="packetLoss" 
                  stroke="#f44336" 
                  name="Packet Loss %" 
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Bandwidth Graph */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Packets & Bytes Sent
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="packetsSent" fill="#2196f3" name="Packets Sent" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">
          No RTCP data available from Homer for this call. This may indicate the call was not captured or RTCP reports were not generated.
        </Alert>
      )}
    </Container>
  );
}
